import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Check, Download, TriangleAlert, Upload, X } from 'lucide-react';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import TradeInStepper from '../../components/TradeInStepper';
import excelTemplateIcon from '../../assets/img/trade-in-listing/excel-template.svg';
import excelFileIcon from '../../assets/img/trade-in-listing/excel-file.svg';
import {
    downloadListingTemplate,
    uploadListingImport,
    getListingImportBatch,
    downloadListingImportValidation,
    completeListingImport,
} from './tradeInMockApi.js';

// ERP-5289 — Trade-In Listing → Import (/trade-in-listing/import): from one Excel, add new
// Listings / Listing Items with their initial prices, update their basic info, and delete the
// ones the Excel no longer has. Prices of existing items are not touched here — Trade-In Pricing
// keeps its own entry point. Same three-step shell as the Platform Listing import
// (listingpricing/PlatformListingImport.js), with Trade-In Pricing's step bar:
//
//   01 Download and Upload  download the Listing Template, drop / browse the edited .xlsx
//   02 Data Validation      each row is Success or Fatal with its reason; Fatal rows are
//                           listed and always skipped, Success rows go on to Step 3
//   03 Complete             summary of new / updated / deleted Listing Items, then Save —
//                           the Trade-In Listing page's banner follows the WooCommerce sync
//
// Calls go through tradeInMockApi.js: download, upload, validation and the Validation Result
// file and Save are real (ERP-5307 / 5310 / 5311 / 5314). Fatal rows are always skipped.
//
// ERP-5296: Discard (any step, while scanning too) asks first, then clears all three steps and
// returns to the Trade-In Listing table. While there is unsaved data — a chosen file, or a
// validated upload — leaving the page asks too: the browser's prompt on reload / close, the
// same Discard modal on in-app navigation.

const YELLOW = '#FFEB00';
const FONT = 'Montserrat, sans-serif';
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const POLL_MS = 1000;
const LIST_PATH = '/trade-in-listing';

// Same step bar as Add/Edit Listing and Trade-In Pricing, with the import's own steps.
const STEPS = ['Download and Upload', 'Data Validation', 'Complete'];
const STEPPER_STEPS = STEPS.map((label, i) => ({ num: String(i + 1).padStart(2, '0'), label }));

const card = { background: '#FFFFFF', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' };
const spinStyle = { display: 'inline-block', width: 16, height: 16, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'tiImportSpin 0.8s linear infinite', flexShrink: 0 };

function formatSize(bytes) {
    return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

// Fatal rows can't be kept (ERP-5311), so Skip is always on — shown, not toggled.
function SkipMark() {
    return (
        <span title="Fatal rows are skipped — fix them in the file and re-upload to import them" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#000000', cursor: 'default' }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: YELLOW, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', flexShrink: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#000000' }} />
            </span>
            Skip
        </span>
    );
}

export default function TradeInListingImport() {
    const history = useHistory();
    const fileInputRef = useRef(null);
    const allowNavigationRef = useRef(false);
    const pollRef = useRef(null);
    // Bumped by Discard / unmount: an upload or poll that answers afterwards is ignored.
    const runRef = useRef(0);

    const [currentStep, setCurrentStep] = useState(1);
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');

    const [isScanning, setIsScanning] = useState(false);
    const [batch, setBatch] = useState(null);
    const [issueDesc, setIssueDesc] = useState(false);
    const [nextError, setNextError] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [discardOpen, setDiscardOpen] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);

    const isDirty = Boolean(file) || Boolean(batch);

    // ── navigation guards ──
    useEffect(() => {
        if (!isDirty) return undefined;
        const h = (e) => {
            if (allowNavigationRef.current) return;   // saved or discarded: leaving is the point
            e.preventDefault();
            e.returnValue = true;   // older browsers (Chrome < 119) only prompt when returnValue is set
        };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [isDirty]);

    useEffect(() => {
        const unblock = history.block((tx) => {
            if (!isDirty || allowNavigationRef.current) return true;
            // Save can't be called back once sent, and it takes the user to the table itself.
            if (isSaving) return false;
            setNextLocation(tx);
            setDiscardOpen(true);
            return false;
        });
        return () => unblock();
    }, [isDirty, isSaving, history]);

    const stopPolling = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
    useEffect(() => () => { runRef.current += 1; stopPolling(); }, []);

    // ── step 1 ──
    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        setDownloadError('');
        try {
            const ok = await downloadListingTemplate();
            if (!ok) setDownloadError('Download failed — please try again');
        } catch (e) {
            setDownloadError('Download failed — please try again');
        } finally {
            setDownloading(false);
        }
    };

    const processFile = (f) => {
        if (!f) return;
        setUploadError('');
        setFieldError('');
        if (!/\.xlsx$/i.test(f.name) || f.size > MAX_FILE_BYTES) {
            setUploadError('Upload failed — Only Excel (.xlsx) file below 100MB is supported');
            return;
        }
        setFile(f);
    };
    const browse = () => fileInputRef.current && fileInputRef.current.click();
    const removeFile = () => { setFile(null); setUploadError(''); if (fileInputRef.current) fileInputRef.current.value = null; };

    // ── step 2 ──
    const pollBatch = async (id, run) => {
        try {
            const data = await getListingImportBatch(id);
            if (run !== runRef.current) return;
            if (data.status === 'validated') {
                setBatch(data);
                setIsScanning(false);
                return;
            }
        } catch (e) {
            // transient — keep asking
        }
        if (run === runRef.current) pollRef.current = setTimeout(() => pollBatch(id, run), POLL_MS);
    };

    const runValidation = async () => {
        const run = ++runRef.current;
        setCurrentStep(2);
        setIsScanning(true);
        setBatch(null);
        setNextError('');
        try {
            const { id } = await uploadListingImport(file);
            if (run === runRef.current) pollBatch(id, run);
        } catch (e) {
            if (run !== runRef.current) return;
            setIsScanning(false);
            setCurrentStep(1);
            setUploadError(e.message || 'Upload failed — please try again');
        }
    };

    const issues = batch ? [...batch.issues].sort((a, b) => (a.row - b.row) * (issueDesc ? -1 : 1)) : [];
    const stats = (batch && batch.statistics) || { total_rows: 0, success_rows: 0, fatal_rows: 0 };

    // ── header buttons ──
    const handleNext = () => {
        if (currentStep === 1) {
            if (!file) { setFieldError('Upload File is required'); return; }
            runValidation();
        } else if (currentStep === 2) {
            if (isScanning || !batch) return;
            // Only Success rows go on to Step 3; with none there is nothing to import.
            if (!stats.success_rows) { setNextError('No row passed validation. Fix the file and re-upload.'); return; }
            setSaveError('');
            setCurrentStep(3);
        }
    };
    const handleBack = () => {
        if (currentStep === 2) { stopPolling(); setIsScanning(false); setBatch(null); setCurrentStep(1); }
        else if (currentStep === 3) setCurrentStep(2);
    };

    const handleSave = async () => {
        if (!batch || isSaving) return;
        setIsSaving(true);
        setSaveError('');
        try {
            await completeListingImport(batch.id);
            allowNavigationRef.current = true;
            history.push(LIST_PATH, { successMessage: 'Listing Items Imported Successfully' });
        } catch (e) {
            setSaveError(e.message || 'Save failed — please try again');
            setIsSaving(false);
        }
    };

    // Discard: every step back to empty — the file, the validated upload, Step 3's summary and
    // any error — and an upload still being scanned is ignored when it answers.
    const clearAllSteps = () => {
        runRef.current += 1;
        stopPolling();
        setCurrentStep(1);
        setFile(null);
        setIsDragging(false);
        setUploadError('');
        setFieldError('');
        setDownloadError('');
        setIsScanning(false);
        setBatch(null);
        setIssueDesc(false);
        setNextError('');
        setSaveError('');
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const confirmDiscard = () => {
        clearAllSteps();
        allowNavigationRef.current = true;
        setDiscardOpen(false);
        // From the Discard button: the table. From a blocked link / Back: where the user was going.
        history.push(nextLocation || LIST_PATH);
        setNextLocation(null);
    };

    const btnBase = { display: 'flex', height: 34, minWidth: 100, padding: '0 10px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' };
    const disabledStyle = { border: '1px solid #ADB5BD', background: '#E9ECEF', color: '#ADB5BD', cursor: 'not-allowed' };
    const lightStyle = { border: '1px solid #212529', background: '#FFFFFF', color: '#212529' };
    const darkStyle = { border: '1px solid #212529', background: '#212529', color: '#FFFFFF' };
    const isBusy = isScanning || isSaving;
    const nextDisabled = isBusy || (currentStep === 2 && !batch);
    const stepTitle = STEPS[currentStep - 1];
    const summary = (batch && batch.summary) || { new: 0, updated: 0, deleted: 0 };
    const sectionLabel = (num, text, required) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#ADB5BD' }}>{num}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>
                {text}{required && <span style={{ color: '#DC3545', paddingLeft: 2 }}>*</span>}
            </span>
        </div>
    );

    return (
        <div className="company-body main-company-body-inner new-look" style={{ fontFamily: FONT }}>
            <style>{'@keyframes tiImportSpin { to { transform: rotate(360deg); } } .ti-scan-ring { width: 88px; height: 88px; box-sizing: border-box; border-radius: 50%; border: 8px solid #E9ECEF; border-top-color: #FFEB00; animation: tiImportSpin 0.9s linear infinite; }'}</style>

            <div className="breadcrumb" style={{ marginBottom: 8 }}>
                <span className="breadcrumb-item">Listing</span>
                <span className="breadcrumb-item clickable" style={{ cursor: 'pointer' }} onClick={() => history.push(LIST_PATH)}>Trade-In Listing</span>
                <span className="breadcrumb-item">Import</span>
            </div>

            <TradeInStepper currentStep={currentStep} steps={STEPPER_STEPS} />

            <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, padding: 20, marginBottom: 12 }}>
                <span style={{ flexGrow: 1, fontSize: 20, fontWeight: 700, color: '#212529' }}>{stepTitle}</span>
                <button type="button" onClick={() => { setNextLocation(null); setDiscardOpen(true); }} disabled={isSaving} style={{ ...btnBase, ...(isSaving ? disabledStyle : lightStyle) }}>Discard</button>
                {currentStep > 1 && (
                    <button type="button" onClick={handleBack} disabled={isBusy} style={{ ...btnBase, ...(isBusy ? disabledStyle : lightStyle) }}>Back</button>
                )}
                {currentStep < 3 ? (
                    <button type="button" onClick={handleNext} disabled={nextDisabled} style={{ ...btnBase, ...(nextDisabled ? disabledStyle : darkStyle) }}>Next</button>
                ) : (
                    <button type="button" onClick={handleSave} disabled={isBusy} style={{ ...btnBase, ...(isBusy ? disabledStyle : darkStyle) }}>
                        {isSaving ? 'Saving…' : 'Save'}
                        {isSaving && <span style={spinStyle} />}
                    </button>
                )}
            </div>

            {/* ── STEP 1 ── */}
            {currentStep === 1 && (
                <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 20, padding: 20 }}>
                    <div style={{ display: 'flex', gap: 20 }}>
                        <div style={{ width: 2, flexShrink: 0, alignSelf: 'stretch', background: YELLOW }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {sectionLabel('01', 'Download Template')}
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: 16, background: '#F8F9FA', border: '1px solid #CED4DA', borderRadius: 10 }}>
                                <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <img src={excelTemplateIcon} alt="" style={{ width: 40, height: 40, flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>Listing Template</span>
                                        <span style={{ fontSize: 14, color: '#212529' }}>Download the latest Listing Template with all Trade-In Listing Items</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    style={{ ...btnBase, height: 40, minWidth: 110, gap: 5, border: 'none', background: downloading ? '#E9ECEF' : '#212529', color: downloading ? '#6C757D' : '#FFFFFF', cursor: downloading ? 'progress' : 'pointer' }}
                                >
                                    Download
                                    {downloading ? <span style={spinStyle} /> : <Download size={18} />}
                                </button>
                            </div>
                            {downloadError && <span style={{ color: '#DC3545', fontSize: 12 }}>{downloadError}</span>}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 20 }}>
                        <div style={{ width: 2, flexShrink: 0, alignSelf: 'stretch', background: YELLOW }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {sectionLabel('02', 'Upload File', true)}
                            <span style={{ fontSize: 14, color: '#212529' }}>Upload the Listing Excel with new or updated basic information of Listing Items</span>
                            <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />
                            <div
                                onClick={!file ? browse : undefined}
                                onDragOver={e => e.preventDefault()}
                                onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                                onDrop={e => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files[0]); }}
                                style={{
                                    position: 'relative', height: 120, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                                    padding: file ? '0 70px' : 0, borderRadius: 10, cursor: file ? 'default' : 'pointer', color: '#6C757D',
                                    border: `1px dashed ${isDragging ? '#212529' : (uploadError || fieldError) ? '#DC3545' : '#6C757D'}`,
                                    background: isDragging ? '#F8F9FA' : '#FFFFFF',
                                }}
                            >
                                {file ? (
                                    <>
                                        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 10, color: '#212529' }}>
                                            <button type="button" title="Replace" onClick={e => { e.stopPropagation(); browse(); }} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#212529' }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" /></svg>
                                            </button>
                                            <button type="button" title="Remove" onClick={e => { e.stopPropagation(); removeFile(); }} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#212529' }}>
                                                <X size={18} />
                                            </button>
                                        </div>
                                        <img src={excelFileIcon} alt="" style={{ width: 62, height: 62 }} />
                                        <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#212529' }}>
                                            {file.name} ({formatSize(file.size)})
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={24} />
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 5, fontSize: 12 }}>
                                            <span>Drag your Excel (.xlsx) here or</span>
                                            <span style={{ color: '#0D6EFD', fontWeight: 600 }}>Browse</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            {(uploadError || fieldError) && <span style={{ color: '#DC3545', fontSize: 12 }}>{uploadError || fieldError}</span>}
                        </div>
                    </div>
                </div>
            )}

            {/* ── STEP 2: scanning ── */}
            {currentStep === 2 && isScanning && (
                <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 20, padding: 20 }}>
                    <div style={{ display: 'flex', gap: 20, padding: 16, background: '#FFFEF0', border: `1px solid ${YELLOW}`, borderRadius: 8 }}>
                        <TriangleAlert size={24} color="#F6C343" style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2, color: '#212529' }}>Do not close or leave this page</span>
                            <span style={{ fontSize: 14, lineHeight: '18px', color: '#212529' }}>Your file is being scanned. Navigating away or closing this page will interrupt the process and you will need to re-upload your file.</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                            <div className="ti-scan-ring" />
                            <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, textAlign: 'center', color: '#212529' }}>Scanning your file for issues...</span>
                        </div>
                        <span style={{ fontSize: 14, textAlign: 'center', color: '#6C757D' }}>This usually take a few seconds.</span>
                    </div>
                </div>
            )}

            {/* ── STEP 2: results ── */}
            {currentStep === 2 && !isScanning && batch && (
                <>
                    <div style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20, padding: 20, marginBottom: 12 }}>
                        <button
                            type="button"
                            onClick={() => downloadListingImportValidation(batch.id)}
                            style={{ ...btnBase, height: 40, minWidth: 110, gap: 5, border: 'none', background: '#212529', color: '#FFFFFF' }}
                        >
                            Validation Result <Download size={18} />
                        </button>
                        <div style={{ width: 1, height: 40, background: '#DEE2E6' }} />
                        <div style={{ height: 40, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #DEE2E6', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                            <span style={{ width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: '#198754', border: '1px solid #000000', borderRadius: 99, color: '#FFFFFF' }}>
                                <Check size={14} />
                            </span>
                            <span style={{ fontSize: 14, color: '#212529' }}>Success: <b style={{ fontWeight: 600 }}>{stats.success_rows}</b></span>
                        </div>
                        {stats.fatal_rows > 0 && (
                            <div style={{ height: 40, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', boxSizing: 'border-box', border: '1px solid #DEE2E6', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                                <span style={{ width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: '#DC3545', border: '1px solid #000000', borderRadius: 99, color: '#FFFFFF' }}>
                                    <X size={14} />
                                </span>
                                <span style={{ fontSize: 14, color: '#212529' }}>
                                    Fatal: <b style={{ fontWeight: 600 }}>{stats.fatal_rows}</b> (Please fix them and re-upload, or skip these rows and continue.)
                                </span>
                            </div>
                        )}
                    </div>

                    {batch.issue_count > 0 ? (
                        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 20, padding: 20 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(300px,1fr) auto', minWidth: 560, fontSize: 12, color: '#212529' }}>
                                    <div onClick={() => setIssueDesc(d => !d)} style={{ height: 45, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 4, padding: 10, background: '#E9ECEF', fontWeight: 700, cursor: 'pointer', userSelect: 'none' }}>
                                        Row #
                                        <span style={{ display: 'flex', flexDirection: 'column' }}>
                                            <svg width={12} height={12} viewBox="0 0 24 24" fill={!issueDesc ? '#212529' : '#6C757D'}><path d="M12 8l6 6H6l6-6z" /></svg>
                                            <svg style={{ marginTop: -7 }} width={12} height={12} viewBox="0 0 24 24" fill={issueDesc ? '#212529' : '#6C757D'}><path d="M12 16l-6-6h12l-6 6z" /></svg>
                                        </span>
                                    </div>
                                    <div style={{ height: 45, boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: 10, background: '#E9ECEF', fontWeight: 700 }}>Issue Details</div>
                                    <div style={{ height: 45, boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: 10, background: '#E9ECEF', fontWeight: 700 }}>Action</div>
                                    {issues.map((r, i) => {
                                        const bg = i % 2 ? '#F8F9FA' : '#FFFFFF';
                                        const rowCell = { minHeight: 45, boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: 10, background: bg, borderBottom: '1px solid #DEE2E6' };
                                        return (
                                            <React.Fragment key={r.id}>
                                                <div style={{ ...rowCell, justifyContent: 'center', color: '#1F1F1F' }}>{r.row}</div>
                                                <div style={rowCell}>{r.issue}</div>
                                                <div style={{ ...rowCell, padding: '10px 20px 10px 10px' }}>
                                                    <SkipMark />
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 12, color: '#212529' }}>
                                <span style={{ color: '#DC3545' }}>{nextError}</span>
                                <span>1 - {issues.length} of {issues.length}</span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ ...card, padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <h5 style={{ fontWeight: 600, fontSize: 20, color: '#212529', margin: '0 0 8px' }}>All Rows are Valid</h5>
                            <p style={{ fontSize: 14, color: '#6C757D', margin: 0 }}>You can proceed to the next step.</p>
                        </div>
                    )}
                </>
            )}

            {/* ── STEP 3 ── */}
            {currentStep === 3 && batch && (
                <div style={{ ...card, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ height: 50, display: 'flex', alignItems: 'center', padding: '0 20px', background: '#F8F9FA', fontSize: 14, fontWeight: 600, color: '#212529' }}>Summary</div>
                    <div style={{ padding: 20 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', fontSize: 12, color: '#212529' }}>
                            {['New Listing Items', 'Updated Listing Items', 'Deleted Listing Items'].map(h => (
                                <div key={h} style={{ height: 45, display: 'flex', alignItems: 'center', padding: '0 10px', background: '#E9ECEF', fontWeight: 700 }}>{h}</div>
                            ))}
                            {[summary.new, summary.updated, summary.deleted].map((n, i) => (
                                <div key={i} style={{ height: 45, display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box', borderBottom: '1px solid #DEE2E6' }}>{n}</div>
                            ))}
                        </div>
                        {saveError && <p style={{ color: '#DC3545', fontSize: 12, margin: '12px 0 0' }}>{saveError}</p>}
                    </div>
                </div>
            )}

            <DiscardConfirmationModal
                isOpen={discardOpen}
                entityName="Trade-In Listing Import"
                onCancel={() => { setDiscardOpen(false); setNextLocation(null); }}
                onConfirm={confirmDiscard}
            />
        </div>
    );
}
